// cart.js (loaded on the cart page) already declares MCartRemoveButton / MCartTemplate as
// top-level lexical bindings. Re-declaring those identifiers throws "Identifier has already
// been declared" and aborts this whole script, which prevents the cart drawer from
// initializing on the cart page. The outer binding names below are intentionally different;
// the inner class expression names are class-scoped and therefore safe to reuse. When cart.js
// has already run (cart page), we reuse its registered classes instead of redefining them.
const getZapietMethodValue = (method) => {
  if (!method) return '';
  if (typeof method === 'string') return method;
  return getZapietMethodValue(method.method || method.selected_method || method.selectedMethod || method.type);
};

const isZapietPickupMethod = (method) => {
  const value = getZapietMethodValue(method).toLowerCase().replace(/[\s-]+/g, '_');
  return value.includes('pickup') || value.includes('pick_up') || value.includes('collection');
};

const MCartRemoveButtonClass =
  customElements.get('m-cart-remove-button') ||
  class MCartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        (this.closest('m-cart') || this.closest('m-cart-drawer-items')).updateQuantity(this.dataset.index, 0);
      });
    }
  };

if (!customElements.get('m-cart-remove-button')) {
  customElements.define('m-cart-remove-button', MCartRemoveButtonClass);
}

const MCartTemplateClass =
  customElements.get('m-cart') ||
  class MCartTemplate extends HTMLElement {
    constructor() {
      super();
      this.cartUpdateUnsubscriber = void 0;
      this.isCartPage = MinimogSettings.templateName === 'cart';
      this.cartDrawerWrapper = document.querySelector('m-cart-drawer');
      this.cartDrawerInner = document.querySelector('.m-cart-drawer__inner');
      this.mainCartItems = this.querySelector('[data-minimog-cart-items]');
      this.cartSubTotal = this.querySelector('[data-cart-subtotal]');
      this.cartDiscount = this.querySelector('[data-minimog-cart-discounts]');
      // Gift wrapping is configured on the cart page, not the cart drawer.
      this.giftWrapping = this.isCartPage ? this.querySelector('[data-minimog-gift-wrapping]') : null;

      let loadingTarget = this.cartDrawerInner;
      if (this.isCartPage) loadingTarget = document.body;

      this.loading = new MinimogLibs.AnimateLoading(loadingTarget, { overlay: loadingTarget });
      this.rootUrl = window.Shopify.routes.root;

      const debouncedOnChange = debounce((event) => {
        if (event.target.name !== 'id') this.onChange(event);
      }, 300);

      if (this.isCartPage) {
        this.mainCartItems.addEventListener('change', debouncedOnChange.bind(this));
      } else {
        this.addEventListener('change', debouncedOnChange.bind(this));
      }

      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
        this.getCart().then((cart) => {
          this.updateCartCount(cart.item_count);
        });
      });
    }

    updateCartCount(itemCount) {
      document.querySelectorAll('.m-cart-count-bubble').forEach((cartCount) => {
        if (itemCount > 0) {
          cartCount.textContent = itemCount;
          cartCount.classList.remove('m:hidden');
        } else {
          cartCount.classList.add('m:hidden');
        }
      });
    }

    getCart() {
      return fetchJSON(this.rootUrl + 'cart.json');
    }

    connectedCallback() {
      this.cartUpdateUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, (event) => {
        if (event.source !== 'main-cart-items') this.onCartUpdate();
      });
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber && this.cartUpdateUnsubscriber();
    }

    onCartUpdate(renderFooter = true) {
      const { routes } = window.MinimogSettings;
      fetch(`${routes.cart}?section_id=cart-template`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const cartItems = html.querySelector('[data-minimog-cart-items]');
          const cartSubTotal = html.querySelector('[data-cart-subtotal]');
          const cartDiscount = html.querySelector('[data-minimog-cart-discounts]');
          const giftWrapping = html.querySelector('[data-minimog-gift-wrapping]');

          if (this.isCartPage) {
            this.mainCartItems.innerHTML = cartItems.innerHTML;
            if (renderFooter) {
              this.cartSubTotal.innerHTML = cartSubTotal.innerHTML;
              this.cartDiscount.innerHTML = cartDiscount.innerHTML;
              if (this.giftWrapping && giftWrapping) {
                this.giftWrapping.innerHTML = giftWrapping.innerHTML;
              }
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });

      this.getCart().then((cart) => {
        this.classList.toggle('m-cart--empty', cart.item_count === 0);
        this.updateCartCount(cart.item_count);
      });
    }

    onChange(event) {
      this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
    }

    updateQuantity(line, quantity, name) {
      this.loading.start();
      const { routes } = window.MinimogSettings;
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          let quantityElement = document.getElementById(`MinimogDrawer-quantity-${line}`);
          if (this.isCartPage) {
            quantityElement = MinimogTheme.config.mqlMobile
              ? document.querySelector(`.MinimogQuantity-${line}.MinimogQuantity-mobile`)
              : document.querySelector(`.MinimogQuantity-${line}.MinimogQuantity-desktop`);
          }

          const items = document.querySelectorAll('.m-cart-item');

          if (parsedState.errors) {
            this.loading.finish();
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('m-cart--empty', parsedState.item_count === 0);
          this.cartDrawerWrapper && this.cartDrawerWrapper.classList.toggle('m-cart--empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          });

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : void 0;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            message = typeof updatedValue > 'u' ? window.MinimogStrings.cartError : window.MinimogStrings.quantityError.replace('{{ quantity }}', updatedValue);
          }

          this.updateLiveRegions(line, message);
          MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { ...parsedState, source: 'main-cart-items' });
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
        })
        .catch(() => {})
        .finally(() => {
          this.loading.finish();
        });
    }

    updateLiveRegions(line, message) {
      let lineItemNode = document.getElementById(`MinimogCartDrawer-Item-${line}`);
      if (this.isCartPage) {
        lineItemNode = document.getElementById(`MinimogCart-Item-${line}`);
      }

      if (message !== '' && lineItemNode) {
        MinimogTheme.Notification.show({ target: lineItemNode, type: 'warning', message });
      }
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        { id: 'MinimogCart', section: 'cart-template', selector: '[data-minimog-cart-items]' },
        { id: 'MinimogCart', section: 'cart-template', selector: '[data-cart-goal]' },
        { id: 'MinimogCart', section: 'cart-template', selector: '[data-cart-subtotal]' },
        { id: 'MinimogCart', section: 'cart-template', selector: '[data-minimog-cart-discounts]' },
        { id: 'MinimogCart', section: 'cart-template', selector: '[data-minimog-gift-wrapping]' },
      ];
    }
  };

if (!customElements.get('m-cart')) {
  customElements.define('m-cart', MCartTemplateClass);
}

class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector('.m-cart-drawer__inner');
    this.cartDrawerCloseIcon = this.querySelector('.m-cart-drawer__close');
    this.cartOverlay = this.querySelector('.m-cart__overlay');
    this.checkoutForm = this.querySelector('#cart-drawer-form');
    this.rootUrl = window.Shopify.routes.root;
    this.zapietCheckoutMethod = '';
    this.zapietInitAttempts = 0;
    this.isSubmittingCheckout = false;
    this.handleZapietMethodChange = this.handleZapietMethodChange.bind(this);
    this.handleCheckoutSubmit = this.handleCheckoutSubmit.bind(this);

    this.setHeaderCartIconAccessibility();
    this.cartDrawerCloseIcon.addEventListener('click', this.close.bind(this));
    this.checkoutForm && this.checkoutForm.addEventListener('submit', this.handleCheckoutSubmit);
    this.initZapietPickupHandling();

    this.addEventListener('click', (event) => {
      if (event.target.closest('.m-cart-drawer__inner') !== this.cartDrawerInner) this.close();
    });
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('.m-cart-icon-bubble').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  initZapietPickupHandling() {
    const zapietEvent = window.ZapietEvent;
    if (zapietEvent && typeof zapietEvent.listen === 'function') {
      zapietEvent.listen('selected_method', this.handleZapietMethodChange);
      return;
    }

    if (this.zapietInitAttempts >= 20) return;
    this.zapietInitAttempts += 1;
    setTimeout(() => this.initZapietPickupHandling(), 250);
  }

  handleZapietMethodChange(method) {
    this.zapietCheckoutMethod = method || '';
    this.classList.toggle('m-cart-drawer--pickup-selected', isZapietPickupMethod(this.zapietCheckoutMethod));
  }

  handleCheckoutSubmit(event) {
    event.preventDefault();
    if (this.isSubmittingCheckout) return;

    this.isSubmittingCheckout = true;
    this.syncCheckoutState()
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (isZapietPickupMethod(this.zapietCheckoutMethod)) {
          this.isSubmittingCheckout = false;
          window.location.href = `${this.rootUrl}cart`;
          return;
        }

        this.submitCheckoutForm();
      });
  }

  getCheckoutControls() {
    return Array.from(this.querySelectorAll('input, select, textarea')).filter((control) => {
      if (!control.name || control.disabled) return false;
      if (control.type === 'button' || control.type === 'submit' || control.type === 'reset' || control.type === 'file') return false;
      if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) return false;
      return true;
    });
  }

  syncExternalCheckoutFields() {
    if (!this.checkoutForm) return;

    this.checkoutForm.querySelectorAll('[data-cart-drawer-synced-field]').forEach((input) => input.remove());

    this.getCheckoutControls().forEach((control) => {
      if (control.form === this.checkoutForm) return;

      const values = control.tagName === 'SELECT' && control.multiple
        ? Array.from(control.selectedOptions).map((option) => option.value)
        : [control.value];

      values.forEach((value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = control.name;
        input.value = value;
        input.setAttribute('data-cart-drawer-synced-field', 'true');
        this.checkoutForm.appendChild(input);
      });
    });
  }

  getCheckoutStatePayload() {
    if (!this.checkoutForm) return null;

    this.syncExternalCheckoutFields();

    const formData = new FormData(this.checkoutForm);
    const payload = {};
    const attributes = {};

    if (formData.has('note')) {
      payload.note = formData.get('note');
    }

    formData.forEach((value, name) => {
      const match = name.match(/^attributes\[(.+)\]$/);
      if (match) attributes[match[1]] = value;
    });

    if (Object.keys(attributes).length) {
      payload.attributes = attributes;
    }

    return Object.keys(payload).length ? payload : null;
  }

  syncCheckoutState() {
    const payload = this.getCheckoutStatePayload();
    if (!payload) return Promise.resolve();

    return fetch(`${window.MinimogSettings.routes.cart_update_url}`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify(payload),
    }).then((response) => {
      if (!response.ok) throw response;
      return response.json();
    });
  }

  submitCheckoutForm() {
    if (!this.checkoutForm) return;

    let checkoutInput = this.checkoutForm.querySelector('input[type="hidden"][name="checkout"][data-cart-drawer-synced-checkout]');
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'checkout';
      checkoutInput.setAttribute('data-cart-drawer-synced-checkout', 'true');
      this.checkoutForm.appendChild(checkoutInput);
    }

    this.checkoutForm.submit();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('m-cart-drawer--active');
    requestAnimationFrame(() => {
      this.style.setProperty('--m-bg-opacity', '0.5');
      this.cartDrawerInner.style.setProperty('--translate-x', '0');
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add('prevent-scroll');
  }

  close() {
    this.style.setProperty('--m-bg-opacity', '0');
    this.cartDrawerInner.style.setProperty('--translate-x', '100%');
    setTimeout(() => {
      this.classList.remove('m-cart-drawer--active');
      document.documentElement.classList.remove('prevent-scroll');
    }, 300);
  }

  renderContents(parsedState) {
    if (this.classList.contains('m-cart--empty')) this.classList.remove('m-cart--empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? this.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.open();
    });
  }

  updateCartCount(itemCount) {
    document.querySelectorAll('.m-cart-count-bubble').forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove('m:hidden');
      } else {
        cartCount.classList.add('m:hidden');
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + 'cart.json');
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector ? this.querySelector(section.selector) : document.getElementById(section.id);
          if (section.block === 'cart-items') {
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          } else if (updateFooter) {
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          }
        });
      })
      .catch((e) => {
        console.error(e);
      });

    this.getCart().then((cart) => {
      this.classList.toggle('m-cart--empty', cart.item_count === 0);
      this.updateCartCount(cart.item_count);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '[data-minimog-cart-items]', block: 'cart-items' },
      { id: 'cart-drawer', selector: '[data-cart-goal]', block: 'cart-footer' },
      { id: 'cart-drawer', selector: '[data-minimog-cart-discounts]', block: 'cart-footer' },
      { id: 'cart-drawer', selector: '[data-cart-subtotal]', block: 'cart-footer' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('m-cart-drawer')) {
  customElements.define('m-cart-drawer', MCartDrawer);
}

class MCartDrawerItems extends MCartTemplateClass {
  getSectionsToRender() {
    return [
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-minimog-cart-items]' },
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-cart-goal]' },
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-minimog-cart-discounts]' },
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-cart-subtotal]' },
    ];
  }
}

if (!customElements.get('m-cart-drawer-items')) {
  customElements.define('m-cart-drawer-items', MCartDrawerItems);
}
